import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, LogOut, Menu, X, Settings, ShieldCheck, Sparkles, ChevronDown, Landmark, FileBarChart, ClipboardCheck, Share2, ScrollText, Building2, SearchCode } from "lucide-react";
import { useApp } from "../context/AppContext";
import { BrandLogo } from "./common";

export const modulePath = (m) => ({ ai_assist: "/ai", platform: "/platform", settings: "/settings" }[m.key] || `/m/${m.key}`);
const ICON = { ai_assist: Sparkles, platform: ShieldCheck, settings: Settings };
const SA_PAGES = [["/gov-admin", "Government Monitoring Administration", Building2, "gov-admin"], ["/platform", "Central LGU & Platform Administration", ShieldCheck, "platform"], ["/settings", "System Settings", Settings, "settings"]];

function GovNav({ onNavigate, link }) {
  const { meta } = useApp();
  const items = [["/gov", meta.gov.level === "provincial" ? "Provincial Dashboard" : "Municipal Dashboard", Landmark, "dashboard"],
    ["/gov/query", "Population & Household Data Query", SearchCode, "query"],
    ["/gov/reports", "Reports & Analytics", FileBarChart, "reports"], ["/gov/completeness", "Data Completeness & Compliance", ClipboardCheck, "completeness"],
    ["/gov/sharing", "Data-Sharing Status", Share2, "sharing"], ...(meta.gov.is_admin ? [["/gov/audit", "Audit Logs", ScrollText, "audit"]] : [])];
  return (
    <nav aria-label="Government monitoring" className="h-full overflow-y-auto px-3 py-4" data-testid="sidebar-nav">
      <p className="overline-label px-3 mb-1.5">Government monitoring</p>
      {items.map(([to, l, Icon, k]) => <NavLink key={to} end to={to} className={link} onClick={onNavigate} data-testid={`gov-nav-${k}`}><Icon className="h-4 w-4" />{l}</NavLink>)}
      <p className="px-3 mt-4 text-[11px] text-slate-500">Barangays and municipalities are reached from the dashboard tables. Read-only oversight access.</p>
    </nav>
  );
}

function Sidebar({ onNavigate }) {
  const { meta } = useApp();
  const groups = {};
  meta.modules.forEach((m) => { (groups[m.group] = groups[m.group] || []).push(m); });
  const link = ({ isActive }) => `flex items-center gap-2 rounded-sm px-3 py-1.5 text-[13px] transition-colors duration-150 ${isActive ? "nav-active" : "text-slate-700 hover:bg-slate-100"}`;
  if (meta.gov) return <GovNav onNavigate={onNavigate} link={link} />;
  if (meta.user.role === "super_admin") {
    return (
      <nav aria-label="Administration" className="h-full overflow-y-auto px-3 py-4 space-y-1" data-testid="sidebar-nav">
        <p className="overline-label px-3 mb-1.5">Platform administration</p>
        {SA_PAGES.map(([to, label, Icon, id]) => <NavLink key={to} to={to} className={link} onClick={onNavigate} data-testid={`sidebar-item-${id}`}><Icon className="h-4 w-4" /> {label}</NavLink>)}
      </nav>
    );
  }
  return (
    <nav aria-label="Modules" className="h-full overflow-y-auto px-3 py-4" data-testid="sidebar-nav">
      <NavLink end to="/" className={link} onClick={onNavigate} data-testid="sidebar-item-dashboard">
        <LayoutDashboard className="h-4 w-4" /> Dashboard
      </NavLink>
      {Object.entries(groups).map(([g, mods]) => (
        <div key={g} className="mt-5">
          <p className="overline-label px-3 mb-1.5">{g}</p>
          {mods.map((m) => {
            const Icon = ICON[m.key];
            return (
              <NavLink key={m.key} to={modulePath(m)} className={link} onClick={onNavigate} data-testid={`sidebar-item-${m.key}`}>
                <span className="font-mono text-[10px] text-slate-400 w-5">{String(m.no).padStart(2, "0")}</span>
                <span className="truncate flex-1">{m.name}</span>
                {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
              </NavLink>
            );
          })}
        </div>
      ))}
      {meta.user.role === "super_admin" && (
        <NavLink to="/gov-admin" className={link} onClick={onNavigate} data-testid="sidebar-item-gov-admin"><Building2 className="h-4 w-4" /> Government Monitoring Admin</NavLink>
      )}
    </nav>
  );
}

function UserMenu() {
  const { meta, logout } = useApp();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <div className="relative">
      <button data-testid="user-menu-btn" onClick={() => setOpen(!open)} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-white/10" aria-expanded={open}>
        <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold">{meta.user.name[0]}</div>
        <div className="hidden md:block text-left leading-tight">
          <p className="text-sm font-medium" data-testid="header-user-name">{meta.user.name}</p>
          <p className="text-[11px] opacity-80" data-testid="header-user-role">{meta.user.role_label}</p>
        </div>
        <ChevronDown className="h-4 w-4 opacity-80" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-md border bg-white text-slate-800 shadow-lg z-50 py-1" data-testid="user-menu">
          <p className="px-3 py-2 text-xs text-slate-500 truncate">{meta.user.email}</p>
          <button data-testid="profile-settings-link" onClick={() => { setOpen(false); nav("/profile"); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50">
            <ShieldCheck className="h-4 w-4" /> Profile & security
          </button>
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Shell() {
  const { meta } = useApp();
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  const b = meta.branding;
  if (meta.user.role === "super_admin" && !["/gov-admin", "/platform", "/settings", "/profile"].includes(loc.pathname)) return <Navigate to="/gov-admin" replace />;
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 h-16 bg-brand flex items-center gap-3 px-4 shadow-sm" data-testid="app-header">
        <button className="lg:hidden p-1" onClick={() => setMobile(true)} aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        <BrandLogo branding={b} size={38} testid="header-logo" />
        <div className="min-w-0 leading-tight flex-1">
          <p className="font-display font-bold text-[15px] truncate" data-testid="header-lgu-name">{b.lgu_name}</p>
          <p className="text-[12px] opacity-85 truncate" data-testid="header-system-name">{b.system_name}</p>
        </div>
        <div className="hidden sm:block h-8 w-1 rounded-full bg-brand2" aria-hidden />
        <UserMenu />
      </header>
      <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-72 border-r bg-white">
        <Sidebar />
      </aside>
      {mobile && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setMobile(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl">
            <div className="flex justify-end p-2"><button onClick={() => setMobile(false)} aria-label="Close menu" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button></div>
            <Sidebar onNavigate={() => setMobile(false)} />
          </aside>
        </div>
      )}
      <main className="lg:pl-72">
        <div className="px-4 sm:px-8 py-6 max-w-[1500px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
